import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IonPage, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import {
  AppFooter_v2 as AppFooter,
  AppHeader_v2 as AppHeader,
  Button_v2 as Button,
  Container_v2 as Container,
  IconArrowLeft,
  IconButton,
  ScreenInput,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { PublicRoutes } from '../../../routes/navigation';
import { useIsKeyBoardShown, validateEmail } from '../../../shared';
import { setSignUpEmail } from '../../../redux/auth/reducer';
import { useAuthFlow } from 'src/shared/hooks/useAuthFlow';
import useToast from 'src/context/ToastContext/ToastContext';

const ForgotPassword: FC<RouteComponentProps> = ({ history }) => {
  const { t } = useTranslation('auth');
  const { sendPasswordResetEmailToUser } = useAuthFlow();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const isKeyboardShown = useIsKeyBoardShown();

  const emailRef = useRef<HTMLInputElement | null>(null);

  const [email, setEmail] = React.useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  useIonViewDidEnter(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, [emailRef]);

  const handleSubmit = useCallback(async () => {
    if (isDisabled) return;

    try {
      setIsLoading(true);
      await sendPasswordResetEmailToUser({
        email,
      });
      dispatch(setSignUpEmail(email));
      history.push(PublicRoutes.FORGOT_PASSWORD_SUCCESS);
    } catch (error) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  }, [sendPasswordResetEmailToUser, email, isDisabled]);

  useIonViewDidLeave(() => {
    setEmail('');
  });

  useEffect(() => {
    setIsDisabled(!validateEmail(email));
  }, [email]);

  return (
    <IonPage>
      <AppHeader>
        <IconButton icon={<IconArrowLeft />} routerLink={PublicRoutes.PASSWORD} />
      </AppHeader>
      <Container isFlexContainer>
        <Typography variant={TypographyVariantsEnum.Title28Semi} className={'mb-1'}>
          {t('auth.forgot.password.title')}
        </Typography>
        <Typography variant={TypographyVariantsEnum.Text15Regular} opacity={50}>
          {t('auth.forgot.password.description')}
        </Typography>
        <ScreenInput
          ref={emailRef}
          type={'email'}
          name={'email'}
          placeholder={t('auth.forgot.password.placeholder')}
          value={email}
          className={'px-5'}
          autoComplete={'email'}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          required
        />
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        <Button isLoading={isLoading} disabled={isDisabled} fullWidth size={'large'} onClick={handleSubmit}>
          {t('auth.forgot.password.button')}
        </Button>
      </AppFooter>
    </IonPage>
  );
};

export default ForgotPassword;
